// Column widths of a GFM table (`widths`, in em), as pure functions: no DOM, no
// React, so the node view (table-view.ts) and the overlay (table-overlay.tsx)
// share them and tests need only values.
//
// Widths are in em so columns follow the text size. The grips report px, so
// they are converted here before writing back.
//
// A table is never narrower than the body (`min-width: 100%` in the CSS: a
// narrow table put the "+" strip and its columns in odd places). When the
// widths add up to less than the body, the browser spreads the rest in
// proportion to the widths. That body width is `fillEm` / `fillPx` here.

/** The narrowest a grip can make a column (em). */
export const MIN_COLUMN_EM = 3;

/**
 * A total this much over the body still counts as "fits the body" (em).
 * Measured widths are floored to 0.1em, so a fitting table usually adds up to
 * a bit less; it only goes over by rounding or when the minimum lifts a column.
 */
const FILL_EPS = 0.1;

/** One decimal (as written in the option line). */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Floors to one decimal. Used for values fitted to the body: rounding up would
 * make the total 1px wider than the body and add a useless sideways scroll
 * (min-width evens out what is floored away).
 */
function floor1(n: number): number {
  return Math.floor(n * 10 + 1e-6) / 10;
}

function sum(widths: number[]): number {
  return widths.reduce((a, b) => a + b, 0);
}

/** px to em, one decimal. */
export function pxToEm(px: number, fontSizePx: number): number {
  return round1(px / fontSizePx);
}

/**
 * Measured column widths (px) as em widths, at least {@link MIN_COLUMN_EM}.
 * Floored, so a table stretched to the body and written back never adds up
 * wider than the body (which would show a few px of sideways scroll).
 */
export function widthsFromMeasured(px: number[], fontSizePx: number): number[] {
  return px.map((p) => Math.max(MIN_COLUMN_EM, floor1(p / fontSizePx)));
}

/**
 * The widths (em) after moving the right border of column `index` by
 * `deltaEm`. `widths` are the widths shown when grabbed (measured), `fillEm` is
 * the body width.
 *
 * - A table that fits the body (total <= body): a border between columns
 *   trades width with the next column (the total stays; as in Google Docs). The
 *   last grip widens the table.
 * - A table wider than the body: only the grabbed column grows or shrinks and
 *   the total changes ("adjust by the table's whole width"). Once it shrinks to
 *   the body width, the next column grows instead (no table narrower than the
 *   body).
 * - The last grip widens the table (scrolling sideways when wider than the
 *   body) and shrinks it only down to the body width.
 *
 * No column goes below {@link MIN_COLUMN_EM}.
 */
export function resizeColumn(
  widths: number[],
  index: number,
  deltaEm: number,
  fillEm: number,
): number[] {
  const out = [...widths];
  const current = out[index];
  if (current === undefined) return out;
  const delta = round1(deltaEm);
  const total = sum(widths);
  const others = total - current;
  const isLast = index === widths.length - 1;

  if (isLast) {
    // Not narrower than the body. A table that already fits (is short of the
    // body) does not shrink at all (the floor is the current width).
    const floor = Math.min(
      current,
      Math.max(MIN_COLUMN_EM, floor1(fillEm - others)),
    );
    out[index] = Math.max(floor, round1(current + delta));
    return out;
  }

  const next = out[index + 1] ?? 0;
  const filled = total <= fillEm + FILL_EPS;
  if (delta >= 0) {
    if (filled) {
      // Taken from the next column (down to its minimum).
      const take = Math.min(delta, Math.max(0, round1(next - MIN_COLUMN_EM)));
      out[index] = round1(current + take);
      out[index + 1] = round1(next - take);
    } else {
      out[index] = round1(current + delta);
    }
    return out;
  }
  // Shrinking: first what the total can absorb (how far it is over the body),
  // the rest goes to the next column.
  const shrink = Math.min(-delta, Math.max(0, round1(current - MIN_COLUMN_EM)));
  const alone = Math.min(shrink, floor1(Math.max(0, total - fillEm)));
  out[index] = round1(current - shrink);
  out[index + 1] = round1(next + (shrink - alone));
  return out;
}

/** The drawn width (px) of a table with these widths: the body width when narrower. */
export function displayedTotalPx(
  widths: number[],
  fontSizePx: number,
  fillPx: number,
): number {
  return Math.max(sum(widths) * fontSizePx, fillPx);
}

/**
 * Where the right border of column `index` is drawn (px from the table's left)
 * with these widths. When the total is short of the body, the browser spreads
 * the rest in proportion to the widths (`table-layout: fixed` +
 * `min-width: 100%`; measured in Chromium and WebKit), so the same ratio is
 * applied. A grip placed here while dragging matches the border after release.
 */
export function columnEdgePx(
  widths: number[],
  index: number,
  fontSizePx: number,
  fillPx: number,
): number {
  const total = sum(widths) * fontSizePx;
  const scale = total > 0 && total < fillPx ? fillPx / total : 1;
  return sum(widths.slice(0, index + 1)) * fontSizePx * scale;
}
